// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Which input the player is actually using, and the moment it changes (M5-04, XR-008).
//
// A Quest reports hands and controllers through the same `inputSources` list, and a player who
// puts a controller down does not tell the application — the runtime simply stops reporting it
// as tracked, some number of frames later, and starts reporting a hand. Deciding which of the
// two is "the" input every frame from the raw list produces a control scheme that flickers.
//
// So this is a small state machine with two properties that matter:
//
// - **Hysteresis in time, not in confidence.** A method has to be the only one tracked for a
//   continuous `SWITCH_HOLD_MS` before it wins. Picking up a controller puts a hand and a
//   controller in the list at once for as long as it takes to close a fist around it, and a
//   switch on the first frame of overlap would fire twice.
// - **A switch cancels grabs.** 06 §2.5 is explicit: an in-progress grab — the plunger, held —
//   is released safely rather than left stuck to an input source that no longer exists. This
//   reports the edge; the plunger owns what to do about it.
//
// The toast text lives here rather than in the shell because it names the *control*, not the
// device: "reach for the flipper buttons" versus "trigger to flip" is the difference between the
// two schemes, and it is the only sentence the player gets.

/** The contract's vocabulary, imported rather than restated. See `wireInputMethod`. */
import type { InputMethod as WireInputMethod } from '../net/contract'

/** What the player is holding, or not. */
export type InputMethod = 'hands' | 'controllers'

/** One tracked input source, reduced to the two facts this decision needs. */
export interface TrackedSource {
  /** `hand` when the runtime reports joints for it, otherwise a controller. */
  readonly kind: InputMethod
  /**
   * Is the runtime reporting a live pose for it this frame?
   *
   * A controller sitting on a desk is still in `inputSources` and is emphatically not being
   * used, which is exactly the case this whole file exists for.
   */
  readonly tracked: boolean
}

/**
 * How long one method must be the only one tracked before the scheme changes.
 *
 * 400 ms is long enough to cover the overlap of a hand closing around a controller and short
 * enough that a player who has picked one up and is waiting to flip does not notice a delay.
 */
export const SWITCH_HOLD_MS = 400

/**
 * This module's vocabulary as the wire's (M9-38).
 *
 * **Two `InputMethod` types, and they are not the same type.** This one is about *how a headset is
 * being held* — hands or controllers, the only two an XR runtime reports — and
 * `src/net/contract.ts`'s is about *what a score was played with*, which also has a keyboard, a
 * touchscreen and a cabinet in it. The join has to exist somewhere and it existed nowhere: the
 * browser submitted every game as `keyboard` or `touch` no matter what it was played on, so 06
 * §2.1's leaderboard filter has never been able to separate hand play from controller play, and
 * the two most interesting columns on the board have always been empty.
 */
export function wireInputMethod(method: InputMethod): WireInputMethod {
  return method === 'hands' ? 'xr-hands' : 'xr-controllers'
}

/** The sentence shown when the scheme changes. */
export function switchToast(method: InputMethod): string {
  return method === 'hands'
    ? 'Hand tracking active — reach for the flipper buttons'
    : 'Controllers active — trigger to flip'
}

/** What changed on this frame. */
export interface MethodChange {
  readonly method: InputMethod
  readonly toast: string
  /** True when a switch happened and any held grab must be let go (06 §2.5). */
  readonly cancelGrabs: boolean
}

export class InputMethodTracker {
  private active: InputMethod
  /** The method currently building up a case for itself, and for how long. */
  private candidate: InputMethod | undefined
  private candidateMs = 0
  /** True until the first frame that saw anything, so the first method arrives without a toast. */
  private settled = false

  /**
   * Starts on controllers, and that default is deliberate rather than arbitrary: a headset with
   * hand tracking switched off never reports a hand, so a hands-first default would leave a
   * controller player looking at a toast telling them to reach for buttons they cannot press.
   * The reverse mistake corrects itself within `SWITCH_HOLD_MS` of the player raising a hand.
   */
  constructor(initial: InputMethod = 'controllers') {
    this.active = initial
  }

  get method(): InputMethod {
    return this.active
  }

  /**
   * Feed one frame's input sources. Returns a change, or undefined on the vast majority of
   * frames where nothing happened.
   *
   * The **first** frame that sees a tracked source adopts it silently. A toast on the frame the
   * session starts is a toast the player is still adjusting the headset for.
   */
  advance(sources: readonly TrackedSource[], frameMs: number): MethodChange | undefined {
    const hands = sources.some((s) => s.kind === 'hands' && s.tracked)
    const controllers = sources.some((s) => s.kind === 'controllers' && s.tracked)

    // Both or neither: no evidence for a change. Hold whatever is active and reset the timer, so
    // an overlap has to end before a switch can begin rather than accumulating through it.
    if (hands === controllers) {
      this.candidate = undefined
      this.candidateMs = 0
      return undefined
    }

    const seen: InputMethod = hands ? 'hands' : 'controllers'

    if (!this.settled) {
      this.settled = true
      this.candidate = undefined
      this.candidateMs = 0
      if (seen === this.active) return undefined
      this.active = seen
      return undefined
    }

    if (seen === this.active) {
      this.candidate = undefined
      this.candidateMs = 0
      return undefined
    }

    if (this.candidate !== seen) {
      this.candidate = seen
      this.candidateMs = 0
    }
    this.candidateMs += frameMs
    if (this.candidateMs < SWITCH_HOLD_MS) return undefined

    this.active = seen
    this.candidate = undefined
    this.candidateMs = 0
    return { method: seen, toast: switchToast(seen), cancelGrabs: true }
  }
}
